import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { AccompanimentSection } from "@/components/AccompanimentSection";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

const DATA = {
  fr: {
    overline: "Vous avez des questions ?", title: "Foire aux questions",
    more: "Une autre question ?", moret: "Notre équipe se tient à votre disposition.", contact: "Nous contacter",
    faqs: [
      { q: "Comment se déroule l'accompagnement ?", a: "Après votre réservation, un conseiller évalue votre profil, établit la liste des documents nécessaires, prépare et organise votre dossier avec vous et vous guide pas à pas jusqu'à la finalisation de votre démarche." },
      { q: "Qui effectue la validation finale de ma démarche ?", a: "Notre équipe vous accompagne dans la préparation et l'organisation complète de votre dossier et reste à vos côtés jusqu'à la finalisation. Pour votre sécurité et afin que vous gardiez le contrôle de vos informations personnelles, vous effectuez simplement la validation finale directement sur la plateforme officielle concernée, tout en bénéficiant de notre accompagnement." },
      { q: "Quels documents dois-je fournir ?", a: "Généralement votre passeport, un CV, des justificatifs et éventuellement des diplômes. La liste exacte vous est communiquée dans votre espace client après réservation." },
      { q: "Le paiement est-il sécurisé ?", a: "Oui. Tous les paiements sont traités par Stripe, leader mondial du paiement en ligne, avec un chiffrement de niveau bancaire. Nous n'avons jamais accès à vos données de carte." },
      { q: "Puis-je suivre l'avancement de mon dossier ?", a: "Absolument. Depuis votre espace client, vous suivez en temps réel les différentes étapes de traitement de votre dossier." },
      { q: "Quelle est la différence entre les packs Canada ?", a: "Le Pack Canada Complet couvre l'ensemble de la procédure, tandis que le Pack Premium ajoute un conseiller dédié, une priorité de traitement et une assistance à l'installation." },
      { q: "Qu'est-ce que l'inscription au tirage Visa Canada ?", a: "Il s'agit de l'inscription officielle au tirage au sort permettant d'obtenir un visa canadien. Nous vérifions votre éligibilité et gérons votre inscription." },
      { q: "Combien de temps prend la procédure ?", a: "Les délais varient selon la destination et le type de visa. Votre conseiller vous communique une estimation dès le début de votre accompagnement." },
      { q: "Puis-je être remboursé ?", a: "Les conditions de remboursement dépendent de l'avancement du dossier. Contactez notre équipe pour toute demande spécifique." },
    ],
  },
  en: {
    overline: "Have questions?", title: "Frequently asked questions",
    more: "Another question?", moret: "Our team is at your disposal.", contact: "Contact us",
    faqs: [
      { q: "How does the support process work?", a: "After your booking, an advisor assesses your profile, sets up the list of required documents, prepares and organises your file with you and guides you step by step until your procedure is finalised." },
      { q: "Who carries out the final validation of my procedure?", a: "Our team supports you in the complete preparation and organisation of your file and stays by your side until the end. For your security, and so that you keep control of your personal information, you simply carry out the final validation directly on the relevant official platform, with our support throughout." },
      { q: "Which documents do I need to provide?", a: "Usually your passport, a CV, supporting documents and possibly diplomas. The exact list is provided in your client area after booking." },
      { q: "Is the payment secure?", a: "Yes. All payments are processed by Stripe, a global leader in online payments, with bank-level encryption. We never have access to your card data." },
      { q: "Can I track my file's progress?", a: "Absolutely. From your client area, you can follow the different processing steps of your file in real time." },
      { q: "What is the difference between the Canada packages?", a: "The Canada Complete Pack covers the whole procedure, while the Premium Pack adds a dedicated advisor, priority processing and settlement assistance." },
      { q: "What is the Canada visa lottery registration?", a: "It is the official registration for the draw allowing you to obtain a Canadian visa. We check your eligibility and manage your registration." },
      { q: "How long does the procedure take?", a: "Timeframes vary depending on the destination and visa type. Your advisor gives you an estimate at the start of your support." },
      { q: "Can I get a refund?", a: "Refund conditions depend on the progress of the file. Contact our team for any specific request." },
    ],
  },
};

export default function FAQ() {
  const { lang } = useLang();
  const d = DATA[lang];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: d.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <div data-testid="faq-page">
      <Seo
        title="FAQ — Questions fréquentes sur nos accompagnements visa | Work & Travel Expert"
        description="Réponses aux questions fréquentes sur nos accompagnements visa et immigration Australie & Canada : déroulement, documents, paiement sécurisé, suivi de dossier et délais."
        keywords="faq visa, questions immigration, accompagnement visa australie canada, paiement sécurisé stripe"
        path="/faq"
        schema={faqSchema}
      />
      <div className="bg-navy">
        <Section className="py-24 text-center">
          <Overline>{d.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-white">{d.title}</h1>
        </Section>
      </div>
      <Section className="py-20 max-w-3xl">
        <Reveal>
          <Accordion type="single" collapsible className="w-full">
            {d.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-border">
                <AccordionTrigger className="text-left font-heading text-lg text-navy hover:text-gold hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed text-base pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <div className="mt-12 text-center bg-[#F8F9FA] p-10 border border-border">
          <h3 className="font-heading text-2xl text-navy">{d.more}</h3>
          <p className="text-muted-foreground mt-2">{d.moret}</p>
          <Link to="/contact" className="inline-block mt-6 bg-gold hover:bg-gold-hover text-white px-8 py-3 font-medium transition-colors">{d.contact}</Link>
        </div>
      </Section>

      {/* VOUS ÊTES ACCOMPAGNÉ À CHAQUE ÉTAPE */}
      <AccompanimentSection />
    </div>
  );
}
